import { Button, Divider, SkeletonBox, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBankTransactionDrawer } from '../../../../../contexts'
import { useBookkeepingItemsContext } from '../../../../../contexts/bookkeepingItemsContext'
import { BookkeepingItem } from '../../BookkeepingItem'
import { BookkeepingItemsListSort } from './elements/BookkeepingItemsListSort'
import { BookkeepingItemsSearch } from './elements/BookkeepingItemsSearch/BookkeepingItemsSearch'

const MAX_TOP_MATCHES = 3

interface BookkeepingItemsListProps {
  showAll?: boolean
  onViewAll?: () => void
}

export const BookkeepingItemsList = ({ showAll = false, onViewAll }: BookkeepingItemsListProps): ReactElement => {
  const { t } = useTranslation()
  const {
    hasBankReconciliationWriteScope,
    openUpgradeModal,
    selectedTransaction,
    selectedBookkeepingItemIds,
    selectedBookkeepingItems,
    toggleBookkeepingItemSelection,
  } = useBankTransactionDrawer()
  const { filteredItems, totalItemsCount, isLoading, getTopMatches } = useBookkeepingItemsContext()

  const topMatches = useMemo(() => {
    if (!selectedTransaction) {
      return []
    }

    const matches = getTopMatches(selectedTransaction, MAX_TOP_MATCHES)
    const matchIds = new Set(matches.map((item) => item.id))

    const missingSelectedItems = selectedBookkeepingItems.filter((item) => !matchIds.has(item.id))

    if (missingSelectedItems.length === 0) {
      return matches
    }

    return [...missingSelectedItems, ...matches].slice(0, MAX_TOP_MATCHES)
  }, [getTopMatches, selectedBookkeepingItems, selectedTransaction])

  const handleCheckedChange = useCallback(
    (id: string) => {
      if (!hasBankReconciliationWriteScope) {
        openUpgradeModal()
        return
      }

      toggleBookkeepingItemSelection(id)
    },
    [hasBankReconciliationWriteScope, openUpgradeModal, toggleBookkeepingItemSelection],
  )

  if (isLoading) {
    return <SkeletonBox fullWidth height={30} />
  }

  if (showAll) {
    if (totalItemsCount === 0) {
      return <></>
    }

    return (
      <div>
        <BookkeepingItemsSearch />
        <Space size="s16" />
        <BookkeepingItemsListSort />
        <Space size="s8" />
        {filteredItems.length === 0 ? (
          <Text colorVariant="discreet">{t('bank_transactions.drawer.no_items')}</Text>
        ) : (
          filteredItems.map((item, index) => (
            <div key={item.id}>
              <BookkeepingItem
                bookkeepingItem={item}
                showCheckbox
                checked={selectedBookkeepingItemIds.has(item.id)}
                onCheckedChange={() => handleCheckedChange(item.id)}
              />
              {index < filteredItems.length - 1 && <Divider />}
            </div>
          ))
        )}
      </div>
    )
  }

  if (!selectedTransaction || totalItemsCount === 0) {
    return <></>
  }

  return (
    <div>
      <Text weight="medium">{t('bank_transactions.drawer.top_matches_title')}</Text>
      <Space size="s8" />
      {topMatches.map((item, index) => (
        <div key={item.id}>
          <BookkeepingItem
            bookkeepingItem={item}
            showCheckbox
            checked={selectedBookkeepingItemIds.has(item.id)}
            onCheckedChange={() => handleCheckedChange(item.id)}
          />
          {index < topMatches.length - 1 && <Divider />}
        </div>
      ))}
      <Space size="s8" />
      {totalItemsCount > MAX_TOP_MATCHES && (
        <Button variant="outlined" fullWidth onClick={onViewAll}>
          {t('bank_transactions.drawer.top_matches_cta')}
        </Button>
      )}
    </div>
  )
}
